import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Form, Button, Row, Col, Spinner, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import { QuestionCircle } from 'react-bootstrap-icons';

// Dev-only "Tune Obstacle Detection" tool. Lets the people building the
// inference service probe the obstacle-detection API with different params,
// see the exact request + raw response, and overwrite the lead's obstacles.
// Gated behind REACT_APP_ENABLE_DEBUG_TOOLS (see the trigger button in
// Obstacles.js).

const DEFAULTS = {
  threshold: 0.05,
  iou: 0.7,
  output_size: 1024,
  device: 'cuda',
  enable_postprocess: true,
  enable_shape_classification: true,
  restrict_to_footprint: true,
};

const FIELD_INFO = {
  threshold: 'Confidence cutoff. Lower = more detections + more false positives.',
  iou: 'Overlap threshold for de-duplicating boxes (NMS).',
  output_size: 'Inference resolution. Higher resolves smaller obstacles (slower).',
  device: 'cpu or cuda (GPU).',
  enable_postprocess: 'Detection post-processing.',
  enable_shape_classification: 'Classify each detection as circle vs polygon.',
  restrict_to_footprint: 'Keep only obstacles inside the roof footprint — uses the exact PREDFTP.png the AI run wrote (1:1 with Run AI Design). Off = raw detections across the whole image.',
};

const InfoTip = ({ id, text }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${id}`}>{text}</Tooltip>}>
    <QuestionCircle className="ml-1 text-muted" style={{ cursor: 'help' }} />
  </OverlayTrigger>
);

InfoTip.propTypes = {
  id: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

function ObstacleTuner({
  show, onClose, runObstacleTune, reloadCanvasObstacles,
}) {
  const [form, setForm] = useState(DEFAULTS);
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState(null); // { ok, statusCode, count, elapsedMs, finishedAt, rawResponse }
  const [error, setError] = useState(null);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Build the request body that will actually be sent. All fields are set in
  // this form (pre-filled with defaults), so we send all of them.
  const requestBody = useMemo(() => ({
    threshold: Number(form.threshold),
    iou: Number(form.iou),
    output_size: Number(form.output_size),
    device: form.device,
    enable_postprocess: form.enable_postprocess,
    enable_shape_classification: form.enable_shape_classification,
    restrict_to_footprint: form.restrict_to_footprint,
  }), [form]);

  const formatFinishedAt = (finishedAt) => {
    if (!finishedAt) return '';
    const d = new Date(finishedAt);
    if (Number.isNaN(d.getTime())) return String(finishedAt);
    return d.toLocaleTimeString();
  };

  const handleRun = () => {
    setIsRunning(true);
    setError(null);
    runObstacleTune(requestBody)
      .then((data) => {
        const raw = data?.raw_response ?? {};
        const count = data?.count ?? raw?.count ?? (Array.isArray(raw?.annotations) ? raw.annotations.length : 0);
        setResult({
          ok: true,
          statusCode: data?.status_code,
          count,
          detected: data?.detected,
          persisted: data?.persisted,
          footprintApplied: data?.footprint_applied,
          elapsedMs: data?.elapsed_ms,
          finishedAt: data?.finished_at,
          rawResponse: raw,
        });
        // Endpoint already persisted the obstacles server-side; reload the
        // canvas so they re-render (reuses the design tool's canvas reload).
        if (typeof reloadCanvasObstacles === 'function') {
          return reloadCanvasObstacles();
        }
        return null;
      })
      .catch((err) => {
        const data = err?.response?.data;
        setError(data?.error || err?.message || 'Obstacle detection failed.');
        if (data) {
          setResult({
            ok: false,
            statusCode: data.status_code ?? err?.response?.status,
            elapsedMs: data.elapsed_ms,
            rawResponse: data.raw_response ?? data,
          });
        }
      })
      .finally(() => setIsRunning(false));
  };

  const renderSummary = () => {
    if (!result) return null;
    const mark = result.ok ? '✓' : '✗';
    const seconds = Number.isFinite(result.elapsedMs) ? `${(result.elapsedMs / 1000).toFixed(1)}s` : '';
    // When the footprint filter ran, show detected → kept (e.g. "12 detected ·
    // 3 kept (footprint)"); otherwise just the persisted count.
    let obstaclesPart = null;
    if (result.ok) {
      if (result.footprintApplied && Number.isFinite(result.detected)) {
        obstaclesPart = `${result.detected} detected · ${result.persisted ?? 0} kept (footprint)`;
      } else {
        obstaclesPart = `${result.persisted ?? result.count ?? 0} obstacles`;
      }
    }
    const parts = [
      `${mark} ${result.statusCode ?? ''}`.trim(),
      obstaclesPart,
      seconds || null,
      formatFinishedAt(result.finishedAt) || null,
    ].filter(Boolean);
    return (
      <div className={result.ok ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'}>
        {parts.join(' · ')}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Tune Obstacle Detection</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col sm={6}>
              <Form.Group>
                <Form.Label>
                  threshold
                  <InfoTip id="threshold" text={FIELD_INFO.threshold} />
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={form.threshold}
                  onChange={(e) => setField('threshold', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group>
                <Form.Label>
                  iou
                  <InfoTip id="iou" text={FIELD_INFO.iou} />
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={form.iou}
                  onChange={(e) => setField('iou', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <Form.Group>
                <Form.Label>
                  output_size
                  <InfoTip id="output_size" text={FIELD_INFO.output_size} />
                </Form.Label>
                <Form.Control
                  type="number"
                  step="1"
                  min="1"
                  value={form.output_size}
                  onChange={(e) => setField('output_size', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group>
                <Form.Label>
                  device
                  <InfoTip id="device" text={FIELD_INFO.device} />
                </Form.Label>
                <Form.Control
                  as="select"
                  value={form.device}
                  onChange={(e) => setField('device', e.target.value)}
                >
                  <option value="cpu">cpu</option>
                  <option value="cuda">cuda</option>
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <Form.Group>
                <Form.Check
                  type="switch"
                  id="enable_postprocess"
                  label={(
                    <>
                      enable_postprocess
                      <InfoTip id="enable_postprocess" text={FIELD_INFO.enable_postprocess} />
                    </>
                  )}
                  checked={form.enable_postprocess}
                  onChange={(e) => setField('enable_postprocess', e.target.checked)}
                />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group>
                <Form.Check
                  type="switch"
                  id="enable_shape_classification"
                  label={(
                    <>
                      enable_shape_classification
                      <InfoTip id="enable_shape_classification" text={FIELD_INFO.enable_shape_classification} />
                    </>
                  )}
                  checked={form.enable_shape_classification}
                  onChange={(e) => setField('enable_shape_classification', e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <Form.Group>
                <Form.Check
                  type="switch"
                  id="restrict_to_footprint"
                  label={(
                    <>
                      restrict_to_footprint
                      <InfoTip id="restrict_to_footprint" text={FIELD_INFO.restrict_to_footprint} />
                    </>
                  )}
                  checked={form.restrict_to_footprint}
                  onChange={(e) => setField('restrict_to_footprint', e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>

        <div className="mt-2">
          <Form.Label className="font-weight-bold mb-1">Request</Form.Label>
          <pre
            className="border rounded bg-light p-2 mb-0"
            style={{ maxHeight: '160px', overflow: 'auto', fontSize: '0.8rem' }}
          >
            {JSON.stringify(requestBody, null, 2)}
          </pre>
        </div>

        {error && !result?.rawResponse && (
          <div className="text-danger mt-3">{error}</div>
        )}

        {result && (
          <div className="mt-3">
            <Form.Label className="font-weight-bold mb-1">Results</Form.Label>
            {renderSummary()}
            <div className="text-muted small mt-1">raw detections — no footprint filter.</div>
            <pre
              className="border rounded bg-light p-2 mt-1 mb-0"
              style={{ maxHeight: '240px', overflow: 'auto', fontSize: '0.8rem' }}
            >
              {JSON.stringify(result.rawResponse, null, 2)}
            </pre>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isRunning}>
          Close
        </Button>
        <Button variant="primary" onClick={handleRun} disabled={isRunning}>
          {isRunning ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" />
              Running...
            </>
          ) : (
            'Run detection'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

ObstacleTuner.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  runObstacleTune: PropTypes.func.isRequired,
  reloadCanvasObstacles: PropTypes.func,
};

export default ObstacleTuner;
